// src/FrameTypes/FrameComp/BoxFrame/BoxProfileSegment.jsx

import {useMemo, useRef, useEffect} from 'react';

export default function BoxProfileSegment({
	totalWidth,
	totalHeight,
	profileWidth,
	profileDepth,
	glassInsert,
	rotation,
	frontMaterial,
	sideMaterial,
}) {
	const segmentFrontRef = useRef();
	const segmentSideRef = useRef();

	const [segmentFront, segmentFrontUV] = useMemo(() => {
		return [
		  createSegmentFrontGeometry(totalWidth, totalHeight, profileWidth, profileDepth),
		  createSegmentFrontUV(totalWidth, totalHeight, profileWidth, profileDepth)
		];
	  }, [totalWidth, totalHeight, profileWidth, profileDepth]);
	  
	  const [segmentSide, segmentSideUV] = useMemo(() => {
		return [
		  createSegmentSideGeometry(totalWidth, totalHeight, profileWidth, profileDepth, glassInsert),
		  createSegmentSideUV(totalWidth, totalHeight, profileWidth, profileDepth, glassInsert)
		];
	  }, [totalWidth, totalHeight, profileWidth, profileDepth]);


	useEffect(() => {
		segmentFrontRef.current.computeVertexNormals();

		if (segmentFrontRef.current) {
			segmentFrontRef.current.attributes.position.needsUpdate = true;
			segmentFrontRef.current.attributes.uv.needsUpdate = true;
		}
	}, [totalWidth, totalHeight, profileWidth, profileDepth]);

	useEffect(() => {
		segmentSideRef.current.computeVertexNormals();

		if (segmentSideRef.current) {
			segmentSideRef.current.attributes.position.needsUpdate = true;
			segmentSideRef.current.attributes.uv.needsUpdate = true;
		}
	}, [totalWidth, totalHeight, profileWidth, profileDepth]);


	return (
		<>
			<mesh
				castShadow
				rotation-z={rotation}
				material={frontMaterial}
				frustumCulled={false}
			>
				<bufferGeometry ref={segmentFrontRef}>
					<bufferAttribute
						attach="attributes-position"
						count={6}
						itemSize={3}
						array={segmentFront}
					/>
					<bufferAttribute
						attach="attributes-uv"
						count={6}
						itemSize={2}
						array={segmentFrontUV}
					/>
				</bufferGeometry>
			</mesh>
			<mesh
				castShadow
				rotation-z={rotation}
				material={sideMaterial}
				frustumCulled={false}
			>
				<bufferGeometry ref={segmentSideRef}>
					<bufferAttribute
						attach="attributes-position"
						count={24}
						itemSize={3}
						array={segmentSide}
					/>
					<bufferAttribute
						attach="attributes-uv"
						count={24}
						itemSize={2}
						array={segmentSideUV}
					/>
				</bufferGeometry>
			</mesh>
		</>
	);
}

function createSegmentFrontGeometry(width, height, pWidth, pDepth) {
	const vertices = new Float32Array([
		-width / 2,             height / 2,             pDepth, 		// 0
		width / 2,              height / 2,             pDepth, 		// 1
		width / 2 + pWidth,     height / 2 + pWidth,    pDepth, 		// 2

		-width / 2,             height / 2,             pDepth, 		// 3
		width / 2 + pWidth,     height / 2 + pWidth,    pDepth, 		// 4
		-width / 2 - pWidth,    height / 2 + pWidth,    pDepth, 		// 5
	]);

	return vertices;
}


function createSegmentFrontUV(width, height, pWidth, pDepth) {
	const uv = new Float32Array([
		-width / 2,             0,          // 0
		width / 2,              0,          // 1
		width / 2 + pWidth,     pWidth,     // 2

		-width / 2,             0,          // 3
		width / 2 + pWidth,     pWidth,     // 4
		-width / 2 - pWidth,    pWidth,     // 5		
	]);

	return uv;
}


function createSegmentSideGeometry(width, height, pWidth, pDepth, insert) {
	const vertices = new Float32Array([
		-width / 2 - pWidth,    height / 2 + pWidth,    pDepth, 		// 0
		width / 2 + pWidth,     height / 2 + pWidth,    pDepth, 		// 1
		width / 2 + pWidth,     height / 2 + pWidth,    0,      		// 2

		-width / 2 - pWidth,    height / 2 + pWidth,    pDepth, 		// 3
		width / 2 + pWidth,     height / 2 + pWidth,    0,      		// 4
		-width / 2 - pWidth,    height / 2 + pWidth,    0,      		// 5

		-width / 2,             height / 2,             pDepth, 		// 6
		width / 2,              height / 2,             pDepth, 		// 7
		width / 2,              height / 2,             pDepth - insert, // 8

		-width / 2,             height / 2,             pDepth, 		// 9
		width / 2,              height / 2,             pDepth - insert, // 10
		-width / 2,             height / 2,             pDepth - insert, // 11

		-width / 2, 			height / 2, 			pDepth - insert, // 12
		width / 2,				height / 2,				pDepth - insert, // 13
		width / 2 + 0.4,		height / 2 + 0.4,		pDepth - insert, // 14

		-width / 2,				height / 2,				pDepth - insert, // 15
		width / 2 + 0.4,		height / 2 + 0.4,		pDepth - insert, // 16
		-width / 2 - 0.4,		height / 2 + 0.4, 		pDepth - insert, // 17

		-width / 2 - 0.4,		height / 2 + 0.4, 		pDepth - insert, // 18
		width / 2 + 0.4,		height / 2 + 0.4, 		pDepth - insert, // 19
		width / 2 + 0.4,		height / 2 + 0.4,		0,				 // 20

		-width / 2 - 0.4, 		height / 2 + 0.4,		pDepth - insert, // 21
		width / 2 + 0.4,		height / 2 + 0.4,		0,				 // 22
		-width / 2 - 0.4,		height / 2 + 0.4,		0,				 // 23
	]);

	return vertices;
}


function createSegmentSideUV(width, height, pWidth, pDepth, insert) {
	const uv = new Float32Array([
		-width / 2 - pWidth,    0,          // 0
		width / 2 + pWidth,     0,          // 1
		width / 2 + pWidth,     pDepth,     // 2

		-width / 2 - pWidth,    0,          // 3
		width / 2 + pWidth,     pDepth,     // 4
		-width / 2 - pWidth,    pDepth,     // 5

		-width / 2,             0,          // 6
		width / 2,              0,          // 7
		width / 2,              insert,        // 8

		-width / 2,             0,          // 9
		width / 2,              insert,        // 10
		-width / 2,             insert,        // 11

		-width / 2 - pWidth,    0,          // 0
		width / 2 + pWidth,     0,          // 1
		width / 2 + pWidth,     pDepth,     // 2

		-width / 2 - pWidth,    0,          // 3
		width / 2 + pWidth,     pDepth,     // 4
		-width / 2 - pWidth,    pDepth,     // 5

		-width / 2,             0,          // 6
		width / 2,              0,          // 7
		width / 2,              insert,        // 8

		-width / 2,             0,          // 9
		width / 2,              insert,        // 10
		-width / 2,             insert,        // 11
	]);

	return uv;
}